import { useState, useRef, useCallback } from 'react';
import { Settings, Zap, Upload, Plus } from 'lucide-react';
import { ImageFile, ResizeSettings } from '@/types';
import ImageUploadArea from './ImageUploadArea';
import SettingsPanel from './SettingsPanel';
import ImageListCompact from './ImageListCompact';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface ImageConverterPageProps {
  images: ImageFile[];
  settings: ResizeSettings;
  isProcessing: boolean;
  onFileSelect: (files: FileList) => void;
  onProcessImage: (image: ImageFile) => void;
  onProcessAll: (imagesToProcess: ImageFile[]) => void;
  onDownloadImage: (image: ImageFile) => void;
  onDownloadAll: () => void;
  onDownloadAllAsZip: () => void;
  onRemoveImage: (id: string) => void;
  onClearAll: () => void;
  onUpdateSettings: (key: keyof ResizeSettings, value: any) => void;
  isDarkMode: boolean;
}

export default function ImageConverterPage({
  images,
  settings,
  isProcessing,
  onFileSelect,
  onProcessImage,
  onProcessAll,
  onDownloadImage,
  onDownloadAll,
  onDownloadAllAsZip,
  onRemoveImage,
  onClearAll,
  onUpdateSettings,
  isDarkMode
}: ImageConverterPageProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasImages = images.length > 0;

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFileSelect(e.target.files);
      // Limpar o input para permitir seleção do mesmo arquivo novamente
      e.target.value = '';
    }
  };

  // Handlers para drag & drop na área Empty State
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileSelect(files);
    }
  }, [onFileSelect]);

  const handleEmptyStateClick = () => {
    fileInputRef.current?.click();
  };

  const handleProcessAll = () => {
    console.log('🚀 BOTÃO CONVERTER TODAS CLICADO!');
    console.log('📊 Estado atual:', {
      totalImages: images.length,
      pendingImages: images.filter(img => img.status === 'pending').length,
      isProcessing,
      images: images.map(img => ({
        id: img.id,
        name: img.file?.name,
        status: img.status,
        hasFile: !!img.file
      }))
    });
    onProcessAll(images);
  };

  return (
    <div className="w-full max-w-none space-y-4">
      {/* Botão de Configurações */}
      <div className="flex justify-end">
        <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Configurações</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" style={{ backgroundColor: '#383D52' }}>
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>Configurações de Conversão</span>
              </DialogTitle>
              <DialogDescription>
                Configure os parâmetros padrão para conversão de imagens
              </DialogDescription>
            </DialogHeader>
            <SettingsPanel 
              settings={settings}
              onUpdateSettings={onUpdateSettings}
              onClose={() => setIsSettingsOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Área de Upload - Mostrar apenas quando não há imagens */}
      {!hasImages && (
        <div className="w-full">
          <ImageUploadArea onFileSelect={onFileSelect} isDarkMode={isDarkMode} />
        </div>
      )}

      {/* Input file oculto para adicionar mais arquivos */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".png,.jpeg,.jpg,.gif,.webp,.bmp,.tiff,.tif,.svg,.ico,.hdr,.psd,.dds,.avif,.heic,.heif,.cur,.rgb,.wbmp,.tga,.jbg,.jp2,.xpm,.jfif,.jpe,.exr,.pcx,.pgm,.ppm,.map,.pdb,.jif,.g3,.xbm,.pbm,.jps,.rgba,.ras,.mtv,.pic,.picon,.jbig,.sixel,.fax,.sgi,.pct,.yuv,.pcd,.g4,.vips,.pfm,.jfi,.pgx,.six,.pnm,.ipl,.pal,.rgf,.mng,.xv,.xwd,.otb,.pam,.viff,.sun,.uyvy,.hrz,.rgbo,.palm,.fts,image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Quick Actions */}
      {hasImages && (
        <div className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#2A2E3F] border-[#4A4F64]' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-2">
            <div className={`p-1.5 rounded-md ${
              isDarkMode ? 'bg-green-500/20' : 'bg-green-50'
            }`}>
              <Zap className={`w-4 h-4 ${
                isDarkMode ? 'text-green-400' : 'text-green-600'
              }`} />
            </div>
            <div>
              <p className={`text-sm ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {images.length} {images.length === 1 ? 'imagem' : 'imagens'}
              </p>
              <p className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Pronto para conversão
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-1.5">
            {/* Botão Adicionar - Sempre visível */}
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              size="sm"
              className="flex items-center space-x-1.5"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Escolher imagens</span>
              <span className="sm:hidden">Adicionar</span>
            </Button>
            
            {/* Botões Converter Todas e Limpar Tudo - Ocultos no Desktop (lg:hidden) */}
            <div className="flex lg:hidden items-center space-x-1.5">
              <Button
                onClick={handleProcessAll}
                disabled={isProcessing}
                className="flex items-center space-x-1.5"
              >
                <Zap className="w-4 h-4" />
                <span>Converter Todas</span>
              </Button>
              
              <Button
                onClick={onClearAll}
                variant="outline"
                disabled={isProcessing}
              >
                Limpar Tudo
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Imagens */}
      {hasImages && (
        <div className="w-full">
          <ImageListCompact 
            images={images}
            onProcessImage={onProcessImage}
            onProcessAll={onProcessAll}
            onDownloadImage={onDownloadImage}
            onDownloadAll={onDownloadAll}
            onDownloadAllAsZip={onDownloadAllAsZip}
            onRemoveImage={onRemoveImage}
            onClearAll={onClearAll}
            onUpdateSettings={onUpdateSettings}
            isProcessing={isProcessing}
            settings={settings}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {/* Empty State com Drag & Drop */}
      {!hasImages && (
        <div 
          className={`text-center py-8 px-4 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-300 ${
            isDragActive
              ? isDarkMode 
                ? 'border-purple-400 bg-purple-500/10' 
                : 'border-purple-500 bg-purple-50'
              : isDarkMode 
                ? 'border-[#4A4F64] bg-[#2A2E3F]/50 hover:border-purple-400/50 hover:bg-purple-500/5' 
                : 'border-gray-300 bg-gray-50 hover:border-purple-400 hover:bg-purple-50'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleEmptyStateClick}
        >
          <Upload className={`w-10 h-10 mx-auto mb-3 transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-purple-400' : 'text-purple-500'
              : isDarkMode ? 'text-gray-500 hover:text-purple-400' : 'text-gray-400 hover:text-purple-500'
          }`} />
          <h3 className={`text-lg font-medium mb-2 transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-purple-300' : 'text-purple-600'
              : isDarkMode ? 'text-white hover:text-purple-300' : 'text-gray-900 hover:text-purple-600'
          }`}>
            {isDragActive ? 'Solte suas imagens aqui' : 'Nenhuma imagem carregada'}
          </h3>
          <p className={`text-sm transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-purple-400' : 'text-purple-500'
              : isDarkMode ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-500'
          }`}>
            {isDragActive ? 'Solte para adicionar à lista' : 'Arraste e solte suas imagens ou clique para selecionar'}
          </p>
        </div>
      )}
    </div>
  );
} 